import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  getAvailableVaults,
  getLinkedVaultIds,
  linkVaultToProgram,
  unlinkVaultFromProgram,
  VaultSummary,
  Vault,
  LinkVaultOptions,
  getVaultsByCompany
} from "../../utils/api/endpoints/vault";
import AccessPasswordModal from "./AccessPasswordModal";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import { UserRole } from "../../utils/api/endpoints/user/credentials";

interface VaultSelectorProps {
  programId: number;
  canEdit?: boolean;
}

/**
 * VaultSelector - Allows users to select which credential vaults to link to a program
 * Vaults are created at the company level and can be linked to multiple programs
 * Now requires access password setup when linking for secure sharing
 */
const VaultSelector: React.FC<VaultSelectorProps> = ({ programId, canEdit = true }) => {
  const { role } = useUserCredentials();

  // Check if user is a sub-user (cannot link vaults - requires master password)
  const isSubUser = role === UserRole.DEVELOPER ||
                    role === UserRole.BUSINESS_MANAGER;

  const [availableVaults, setAvailableVaults] = useState<VaultSummary[]>([]);
  const [linkedVaultIds, setLinkedVaultIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<number | null>(null);

  // Access password modal state
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [selectedVault, setSelectedVault] = useState<Vault | null>(null);
  const [isLoadingVault, setIsLoadingVault] = useState(false);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [vaultsRes, linkedRes] = await Promise.all([
        getAvailableVaults(),
        getLinkedVaultIds(programId)
      ]);
      setAvailableVaults(vaultsRes.vaults || []);
      setLinkedVaultIds(linkedRes.vaultIds || []);
    } catch (err: any) {
      setError(err.message || "Failed to load vaults");
    } finally {
      setIsLoading(false);
    }
  }, [programId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLinkVault = async (vaultId: number) => {
    if (!canEdit) return;
    setIsLoadingVault(true);
    setIsUpdating(vaultId);

    try {
      // Fetch full vault with entries for re-encryption
      const response = await getVaultsByCompany();
      const fullVault = response.vaults.find((v: Vault) => v.vault_id === vaultId);

      if (!fullVault) {
        throw new Error("Vault not found");
      }

      setSelectedVault(fullVault);
      setShowAccessModal(true);
    } catch (err: any) {
      console.error("Error loading vault:", err);
      setError(err.message || "Failed to load vault");
    } finally {
      setIsLoadingVault(false);
      setIsUpdating(null);
    }
  };

  const handleAccessPasswordSubmit = async (options: LinkVaultOptions) => {
    if (!selectedVault) return;

    setIsUpdating(selectedVault.vault_id);
    try {
      await linkVaultToProgram(selectedVault.vault_id, programId, options);
      setLinkedVaultIds((prev) => [...prev, selectedVault.vault_id]);
      setShowAccessModal(false);
      setSelectedVault(null);
    } catch (err) {
      console.error("Error linking vault:", err);
      throw err; // Re-throw to show error in modal
    } finally {
      setIsUpdating(null);
    }
  };

  const handleUnlinkVault = async (vaultId: number) => {
    if (!canEdit) return;
    setIsUpdating(vaultId);
    try {
      await unlinkVaultFromProgram(vaultId, programId);
      setLinkedVaultIds((prev) => prev.filter((id) => id !== vaultId));
    } catch (err) {
      console.error("Error unlinking vault:", err);
    } finally {
      setIsUpdating(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-sm text-gray-500">Loading vaults...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={fetchData}
          className="mt-2 text-sm text-red-700 underline hover:no-underline"
        >
          Try again
        </button>
      </div>
    );
  }

  // Sub-users cannot link vaults - show info message
  if (isSubUser) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="rounded-full bg-amber-100 p-3 mb-3">
            <svg className="h-6 w-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">Credential Linking Restricted</h3>
          <p className="text-xs text-slate-600 max-w-md mb-2">
            Please contact your company's administrator to link credential vaults to this program.
          </p>
          <p className="text-xs text-slate-500 max-w-md">
            Only the Business Owner or Organisation Admin can link credentials by editing the program.
          </p>
          {linkedVaultIds.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-200 w-full">
              <p className="text-xs text-green-600 flex items-center justify-center gap-1">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {linkedVaultIds.length} credential{linkedVaultIds.length !== 1 ? "s" : ""} currently linked
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (availableVaults.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="rounded-full bg-blue-100 p-3 mb-3">
            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-sm font-semibold text-slate-800 mb-1">No Credentials Available</h3>
          <p className="text-xs text-slate-500 max-w-md mb-3">
            Create credentials in the Credential Vault first, then link them to this program.
          </p>
          <Link
            to="/dashboard/vault"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Go to Credential Vault
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-slate-500">
          Select credentials to share with researchers for this program
        </p>
        <Link
          to="/dashboard/vault"
          className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
        >
          Manage Vault →
        </Link>
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {availableVaults.map((vault) => {
          const isLinked = linkedVaultIds.includes(vault.vault_id);
          const isCurrentlyUpdating = isUpdating === vault.vault_id;

          return (
            <div
              key={vault.vault_id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                isLinked
                  ? "border-blue-200 bg-blue-50"
                  : "border-gray-200 bg-white hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${isLinked ? "bg-blue-100" : "bg-gray-100"}`}>
                  <svg className={`w-4 h-4 ${isLinked ? "text-blue-600" : "text-gray-500"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-800">{vault.name}</h4>
                  {vault.description && (
                    <p className="text-xs text-slate-500 line-clamp-1">{vault.description}</p>
                  )}
                </div>
              </div>

              {canEdit && (
                <button
                  onClick={() => isLinked ? handleUnlinkVault(vault.vault_id) : handleLinkVault(vault.vault_id)}
                  disabled={isCurrentlyUpdating || isLoadingVault}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    isCurrentlyUpdating || isLoadingVault
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : isLinked
                        ? "bg-red-100 text-red-600 hover:bg-red-200"
                        : "bg-blue-100 text-blue-600 hover:bg-blue-200"
                  }`}
                >
                  {isCurrentlyUpdating ? (
                    <span className="flex items-center gap-1">
                      <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      ...
                    </span>
                  ) : isLinked ? (
                    "Unlink"
                  ) : (
                    "Link"
                  )}
                </button>
              )}

              {!canEdit && isLinked && (
                <span className="px-2 py-1 text-xs font-medium text-green-600 bg-green-100 rounded">
                  Linked
                </span>
              )}
            </div>
          );
        })}
      </div>

      {linkedVaultIds.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-green-600 flex items-center gap-1">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {linkedVaultIds.length} credential{linkedVaultIds.length !== 1 ? "s" : ""} linked to this program
          </p>
        </div>
      )}

      {/* Access Password Modal */}
      {selectedVault && (
        <AccessPasswordModal
          isOpen={showAccessModal}
          onClose={() => {
            setShowAccessModal(false);
            setSelectedVault(null);
          }}
          onSubmit={handleAccessPasswordSubmit}
          vault={selectedVault}
        />
      )}
    </div>
  );
};

export default VaultSelector;

